// @mui
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
// hooks
import { useLocation, useNavigate } from "react-router-dom";
// icons
import { LuCalendar, LuFilePlus, LuHome } from "react-icons/lu";
import { RiFileList3Line } from "react-icons/ri";
import {
  MdEmail,
  MdMeetingRoom,
  MdCampaign,
  MdPoll,
  MdAccessTime,
  MdSchool,
} from "react-icons/md";
import { paths } from "../../../../routes/paths";
import { useAuth } from "../../../../hooks";

const SIDEBAR_WIDTH = 240;

const NAV_CONFIG = [
  { label: "خانه", icon: <LuHome />, value: paths.root, needsAccess: false },
  {
    label: "درخواست ها",
    icon: <LuFilePlus />,
    value: paths.applications.list,
    needsAccess: false,
  },
  {
    label: "پیام‌ها",
    icon: <MdEmail />,
    value: paths.messages.list,
    needsAccess: false,
  },
  {
    label: "تقویم",
    icon: <LuCalendar />,
    value: paths.calendar.calendar,
    needsAccess: false,
  },
  {
    label: "اتاق جلسه",
    icon: <MdMeetingRoom />,
    value: paths.meetingRoom,
    needsAccess: false,
  },
  {
    label: "اعلانات",
    icon: <MdCampaign />,
    value: paths.announcements.list,
    needsAccess: false,
  },
  {
    label: "نظرسنجی",
    icon: <MdPoll />,
    value: paths.surveys.list,
    needsAccess: false,
  },
  {
    label: "حضور",
    icon: <MdAccessTime />,
    value: paths.attendance.today,
    needsAccess: false,
  },
  {
    label: "آموزش",
    icon: <MdSchool />,
    value: paths.training.list,
    needsAccess: false,
  },
  {
    label: "لیست درخواست ها",
    icon: <RiFileList3Line />,
    value: paths.pendingActions.list,
    needsAccess: true,
  },
];

// Use `left: 0` so MUI's RTL plugin flips it to physical `right: 0`
const SidebarRoot = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    width: SIDEBAR_WIDTH,
    zIndex: theme.zIndex.drawer,
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "-4px 0 24px rgba(0, 0, 0, 0.2)"
        : "-4px 0 24px rgba(0, 0, 0, 0.04)",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: alpha(theme.palette.text.primary, 0.1),
      borderRadius: 2,
    },
  },
}));

const LogoSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 72,
}));

interface NavItemButtonProps {
  $isActive: boolean;
}

const NavItemButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "$isActive",
})<NavItemButtonProps>(({ $isActive, theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(1.5),
  width: "100%",
  padding: theme.spacing(1.25, 2),
  borderRadius: 12,
  textAlign: "right",
  transition: theme.transitions.create(
    ["background-color", "color", "transform"],
    {
      duration: theme.transitions.duration.shorter,
    }
  ),
  color: $isActive ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: $isActive
    ? alpha(theme.palette.primary.main, 0.1)
    : "transparent",
  fontWeight: $isActive ? 700 : 500,
  "&:hover": {
    backgroundColor: $isActive
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.text.primary, 0.04),
  },
  "& .nav-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 10,
    flexShrink: 0,
    backgroundColor: $isActive
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.text.primary, 0.04),
    color: $isActive
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
    transition: theme.transitions.create(
      ["background-color", "color", "transform"],
      {
        duration: theme.transitions.duration.shorter,
      }
    ),
    "& svg": {
      width: 20,
      height: 20,
    },
  },
  "& .nav-label": {
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

export const DesktopSidebar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pathname } = useLocation();

  const isJobHasAccess = user?.roles?.includes("approver");

  const handleNavigate = (path: string) => {
    if (path === pathname) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  return (
    <SidebarRoot>
      <LogoSection>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: 18,
            letterSpacing: -0.5,
          }}
        >
          اتوماسیون
        </Typography>
      </LogoSection>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          p: 1.5,
          flex: 1,
        }}
      >
        {NAV_CONFIG.map(({ value, label, icon, needsAccess }) => {
          if (needsAccess && !isJobHasAccess) return null;
          const isActive = value === pathname;
          return (
            <NavItemButton
              key={value}
              $isActive={isActive}
              onClick={() => handleNavigate(value)}
              disableRipple
            >
              <Box className="nav-icon">{icon}</Box>
              <Typography className="nav-label" component="span">
                {label}
              </Typography>
            </NavItemButton>
          );
        })}
      </Box>
    </SidebarRoot>
  );
};

export { SIDEBAR_WIDTH };
